import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "react-hot-toast";
import { BrowserRouter } from "react-router-dom";

// All three fonts ship with the app (OFL-1.1) and are served from our own
// origin. Nothing is fetched from Google: that would hand every visitor's IP
// address to a third party before they have agreed to anything.
// Figtree is the text face, Bricolage Grotesque the display face (font-display),
// DM Mono the face for card numbers and codes — UX_CONCEPT.md §9.2.
import "@fontsource-variable/figtree";
import "@fontsource-variable/bricolage-grotesque";
import "@fontsource/dm-mono/400.css";
import "@fontsource/dm-mono/500.css";

import App from "./App.tsx";
import "./index.css";
// Backend health checker imported for side-effects (auto-starts monitoring)
import "./utils/backendHealthChecker";
import { log } from "@/lib/log";
import { captureAttribution } from "./utils/attribution";

// Before anything can navigate: the landing address and the referrer are only
// the visitor's own until the first in-app redirect replaces them. Held in
// memory, never stored on the device (utils/attribution.ts).
captureAttribution();

async function ensureFreshServiceWorker() {
  if (import.meta.env.DEV || !("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    let shouldReload = false;

    for (const registration of registrations) {
      await registration.update();

      if (registration.waiting) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
        shouldReload = true;
      }
    }

    if (shouldReload) {
      const reloadFlag = "healthpass-sw-refresh";
      if (!sessionStorage.getItem(reloadFlag)) {
        sessionStorage.setItem(reloadFlag, "1");
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          () => {
            window.location.reload();
          },
          { once: true },
        );
      }
    } else {
      sessionStorage.removeItem("healthpass-sw-refresh");
    }
  } catch (error) {
    console.warn("Service worker refresh check failed:", error);
  }
}

void ensureFreshServiceWorker();

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
    mutations: {
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: "#363636",
                color: "#fff",
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: "#22c55e",
                  secondary: "#fff",
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: "#ef4444",
                  secondary: "#fff",
                },
              },
            }}
          />
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);

// Backend health checker runs automatically on import
// No need to call it explicitly in development - it monitors silently
if (import.meta.env.DEV) {
  log.debug("🔧 Development mode: Backend health monitoring available");
}
